import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

const STORAGE_KEY = 'adminTopModule';

const AdminNavigationContext = createContext({
  menuData: [],
  menuLoading: false,
  menuError: null,
  modules: [],
  selectedModuleKey: '',
  setSelectedModuleKey: () => {},
});

const normalizeCategoryKey = (category) => `cat:${String(category || '').trim().toLowerCase()}`;

const resolveModuleForPath = (modules, pathname) => {
  if (!pathname) return '';
  for (const module of modules) {
    for (const item of module.items || []) {
      if (!item?.path) continue;
      if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
        return module.key;
      }
    }
  }
  return '';
};

const sortModules = (modules) => {
  return [...modules].sort((a, b) => {
    const leftOrder = Number.isFinite(Number(a.order)) ? Number(a.order) : 999;
    const rightOrder = Number.isFinite(Number(b.order)) ? Number(b.order) : 999;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return a.label.localeCompare(b.label);
  });
};

export const deriveAdminModules = (menuData = []) => {
  const categories = Array.isArray(menuData) ? menuData.filter((entry) => entry?.isCategory) : [];
  const topLevelItems = Array.isArray(menuData) ? menuData.filter((entry) => entry && !entry.isCategory) : [];

  const modules = [];
  const dedupeItems = (items = []) => {
    const seen = new Set();
    return items.filter((item) => {
      const key = `${item?.path || ''}|${item?.text || item?.title || ''}`;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  if (topLevelItems.length > 0) {
    modules.push({
      key: 'general',
      label: 'Home',
      items: dedupeItems(topLevelItems),
      type: 'general',
      order: 0,
    });
  }

  const categoryMap = new Map();
  categories.forEach((entry) => {
    const label = entry?.category || entry?.title || 'General';
    const key = normalizeCategoryKey(label);
    const items = Array.isArray(entry.items) ? entry.items : [];
    if (!items.length) return;
    if (!categoryMap.has(key)) {
      categoryMap.set(key, {
        key,
        label,
        items: [],
        type: 'category',
        order: Number.isFinite(Number(entry?.order)) ? Number(entry.order) : 999,
      });
    }
    const existing = categoryMap.get(key);
    existing.items = dedupeItems([...(existing.items || []), ...items]);
  });

  categoryMap.forEach((module) => {
    if (!module.items?.length) return;
    modules.push(module);
  });

  return sortModules(modules);
};

export const AdminNavigationProvider = ({
  menuData = [],
  menuLoading = false,
  menuError = null,
  pathname = '',
  children,
}) => {
  const modules = useMemo(() => deriveAdminModules(menuData), [menuData]);

  const [selectedModuleKey, setSelectedModuleKeyRaw] = useState(() => {
    const stored = (typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || '';
    const storedValid = stored && modules.some((m) => m.key === stored);
    if (storedValid) return stored;

    const byPath = resolveModuleForPath(modules, pathname);
    if (byPath) return byPath;

    return modules[0]?.key || '';
  });

  const setSelectedModuleKey = useCallback((nextKey) => {
    const trimmed = String(nextKey || '').trim();
    setSelectedModuleKeyRaw(trimmed);
    try {
      localStorage.setItem(STORAGE_KEY, trimmed);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!modules.length) return;

    const byPath = resolveModuleForPath(modules, pathname);
    if (byPath && byPath !== selectedModuleKey) {
      setSelectedModuleKey(byPath);
      return;
    }

    if (!selectedModuleKey) {
      const stored = (typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || '';
      const storedValid = stored && modules.some((m) => m.key === stored);
      setSelectedModuleKey(storedValid ? stored : modules[0].key);
    }
  }, [modules, pathname, selectedModuleKey, setSelectedModuleKey]);

  const value = useMemo(
    () => ({
      menuData: Array.isArray(menuData) ? menuData : [],
      menuLoading: Boolean(menuLoading),
      menuError: menuError || null,
      modules,
      selectedModuleKey,
      setSelectedModuleKey,
    }),
    [menuData, menuLoading, menuError, modules, selectedModuleKey, setSelectedModuleKey]
  );

  return <AdminNavigationContext.Provider value={value}>{children}</AdminNavigationContext.Provider>;
};

AdminNavigationProvider.propTypes = {
  menuData: PropTypes.array,
  menuLoading: PropTypes.bool,
  menuError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  pathname: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export const useAdminNavigation = () => useContext(AdminNavigationContext);

export default AdminNavigationContext;
